/**
 * AssignmentBar.tsx
 * One absolutely-positioned assignment bar for the Employee Schedule inline
 * timeline / resource timeline. Colour by kind (theme tokens), hatched when
 * Tentative, outlined when overbooked. Single-click → detail drawer.
 *
 * IMP2-1 (audit #2): optional right-edge EXTEND handle — when the host wires
 * `onExtendPointerDown` (WeekDetailBreakdown's drag-to-extend rows), a 6 px
 * ew-resize grip renders on the bar's right edge. The drag lifecycle itself
 * lives in the host (useAssignmentDrag); this component only renders the grip
 * and the in-flight `extending` treatment (ghost opacity + dashed outline).
 */

import React from 'react';
import { Box, Tooltip, useTheme } from '@mui/material';
import type { EmployeeBar } from '../../services/scheduleEmployeeBars';
import { employeeBarColor, assignmentPrimaryLabel } from './employeeScheduleShared';
import { isContinuousRun, CONTINUOUS_STAFFING_HINT } from '../../utils/runModeRendering';

export interface AssignmentBarProps {
  bar: EmployeeBar;
  leftPct: number;
  widthPct: number;
  height?: number;
  /** Absolute top (px) for lane stacking. When omitted the bar is vertically centred. */
  topPx?: number;
  dimmed?: boolean;
  /** When true the bar can be dragged (HTML5 DnD) to reassign — Weekly Board. */
  draggable?: boolean;
  onClick?: (bar: EmployeeBar, anchor: HTMLElement) => void;
  /**
   * IMP2-1 — drag-to-extend: when set, render the right-edge resize grip and
   * attach this pointer-down handler (the host runs useAssignmentDrag).
   */
  onExtendPointerDown?: React.PointerEventHandler<HTMLDivElement>;
  /** IMP2-1 — true while an extend drag is in flight (ghost treatment). */
  extending?: boolean;
}

export const AssignmentBar: React.FC<AssignmentBarProps> = ({
  bar, leftPct, widthPct, height = 22, topPx, dimmed, draggable, onClick,
  onExtendPointerDown, extending = false,
}) => {
  const theme = useTheme();
  const color = employeeBarColor(theme, bar.kind);
  const tentative = bar.status === 'Tentative';

  // Bar shows the WO/project descriptive label (matches Machine Schedule); the
  // test name moves to the tooltip. Wave 3: the "· N%" allocation suffix is
  // GONE — the bar's span IS the busy days (a covered working day = busy).
  const primary = assignmentPrimaryLabel(bar);
  const label = primary;
  const tip = [
    primary,
    primary !== bar.name ? bar.name : null,
    `${bar.startDate} → ${bar.endDate}`,
    bar.kind === 'Task' ? 'Busy (full day) on the covered working days' : 'Day off (whole days)',
    // RM-4 + lab answer Q2 (2026-07-10): continuous rows cover every working
    // day of the run — say so, and why weekends inside the run stay free.
    bar.kind === 'Task' && isContinuousRun(bar.runMode) ? CONTINUOUS_STAFFING_HINT : null,
    bar.machineCode ? `Machine ${bar.machineCode}` : null,
    bar.workOrderNo ? `WO ${bar.workOrderNo}` : bar.quoteNumber ? `Quote ${bar.quoteNumber}` : null,
    bar.overbooked ? 'Conflict — assigned on a day off this week' : null,
  ].filter(Boolean).join('\n');

  return (
    <Tooltip
      title={<span style={{ whiteSpace: 'pre-line' }}>{tip}</span>}
      arrow
      enterDelay={400}
      disableHoverListener={extending}
    >
      <Box
        role="button"
        tabIndex={0}
        draggable={draggable}
        onDragStart={draggable ? (e) => { e.dataTransfer.setData('text/plain', bar.assignmentKey); e.dataTransfer.effectAllowed = 'move'; } : undefined}
        onClick={(e) => onClick?.(bar, e.currentTarget)}
        onKeyDown={(e) => { if (e.key === 'Enter') onClick?.(bar, e.currentTarget); }}
        sx={{
          position: 'absolute',
          left: `${leftPct}%`,
          width: `max(${widthPct}%, 1.5%)`,
          top: topPx !== undefined ? `${topPx}px` : '50%',
          transform: topPx !== undefined ? 'none' : 'translateY(-50%)',
          height,
          borderRadius: '5px',
          px: 0.75,
          display: 'flex',
          alignItems: 'center',
          cursor: onClick ? 'pointer' : 'default',
          color: theme.palette.getContrastText(color),
          fontSize: '0.7rem',
          fontWeight: 600,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          opacity: dimmed ? 0.4 : extending ? 0.5 : tentative ? 0.85 : 1,
          backgroundColor: color,
          backgroundImage: tentative
            ? `repeating-linear-gradient(45deg, rgba(255,255,255,0.28) 0 5px, transparent 5px 10px)`
            : 'none',
          border: bar.overbooked
            ? `2px solid ${theme.schedule.heatmapHigh}`
            : bar.conflictType === 'hard'
              ? `2px solid ${theme.schedule.conflictHard}`
              : '1px solid rgba(0,0,0,0.12)',
          boxShadow: '0 1px 2px rgba(0,0,0,0.12)',
          transition: 'opacity 120ms ease, box-shadow 120ms ease',
          '&:hover': { boxShadow: '0 2px 6px rgba(0,0,0,0.22)' },
          ...(extending && { outline: `2px dashed ${color}`, outlineOffset: '1px' }),
        }}
      >
        {label}

        {/* IMP2-1 — right-edge drag-to-extend grip (host runs useAssignmentDrag).
            touch-action:none so touch/pen drags aren't intercepted by the browser. */}
        {onExtendPointerDown && (
          <Box
            onPointerDown={(e) => {
              e.stopPropagation(); // don't open the detail drawer
              onExtendPointerDown(e);
            }}
            onClick={(e) => e.stopPropagation()}
            sx={{
              position: 'absolute',
              right: 0,
              top: 0,
              bottom: 0,
              width: 6,
              cursor: 'ew-resize',
              backgroundColor: 'rgba(255,255,255,0.3)',
              borderRadius: '0 5px 5px 0',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.55)' },
              flexShrink: 0,
              zIndex: 2,
              touchAction: 'none',
            }}
            aria-label="Drag to extend assignment"
          />
        )}
      </Box>
    </Tooltip>
  );
};
